'use client'

import { useState } from 'react'
import { ChevronDown } from 'lucide-react'

export interface FAQItem {
  id: string
  question: string
  answer: string
  category?: string
}

interface FAQProps {
  items: FAQItem[]
  className?: string
  title?: string
  subtitle?: string
}

interface FAQItemComponentProps {
  item: FAQItem
  isOpen: boolean
  onToggle: () => void
}

function FAQItemComponent({ item, isOpen, onToggle }: FAQItemComponentProps) {
  return (
    <div className="glass-card mb-4 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 text-left bg-transparent hover:bg-white/10 transition-colors duration-200 flex items-center justify-between"
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${item.id}`}
      >
        <h3 className="text-lg font-medium text-white pr-4">
          {item.question}
        </h3>
        <ChevronDown
          className={`w-5 h-5 text-white/70 transition-transform duration-200 flex-shrink-0 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>
      
      <div
        id={`faq-answer-${item.id}`}
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 pb-4 pt-2 text-white/80 leading-relaxed bg-white/5 border-t border-white/10">
          <div dangerouslySetInnerHTML={{ __html: item.answer }} />
        </div>
      </div>
    </div>
  )
}

export default function FAQ({ items, className = '', title, subtitle }: FAQProps) {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set())

  const toggleItem = (id: string) => {
    setOpenItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const expandAll = () => {
    setOpenItems(new Set(items.map(item => item.id)))
  }

  const collapseAll = () => {
    setOpenItems(new Set())
  }

  if (!items.length) {
    return null
  }

  return (
    <section className={`py-20 relative overflow-hidden ${className}`}>
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src="/images/napkin-selection-9.png" 
          alt="" 
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-eskerium-navy via-eskerium-navy/95 to-eskerium-navy"></div>
      </div>
      
      <div className="section-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Side - Header and Image */}
          <div className="space-y-8">
            {(title || subtitle) && (
              <div className="text-center lg:text-left">
                {title && (
                  <h2 className="heading-lg text-white mb-4">
                    {title}
                  </h2>
                )}
                {subtitle && (
                  <p className="text-xl text-white/70">
                    {subtitle}
                  </p>
                )}
              </div>
            )}
            
            {/* FAQ Image */}
            <div className="glass-card p-6">
              <img 
                src="/images/napkin-selection-9.png" 
                alt="FAQ Illustration" 
                className="w-full h-64 object-cover rounded-lg"
              />
            </div>
          </div>
          
          {/* Right Side - FAQ Items */}
          <div className="space-y-4">
            {/* Controls */}
            <div className="flex justify-end mb-6 space-x-4">
              <button
                onClick={expandAll}
                className="text-sm text-eskerium-blue hover:text-eskerium-blue/80 transition-colors duration-200"
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="text-sm text-eskerium-blue hover:text-eskerium-blue/80 transition-colors duration-200"
              >
                Collapse All
              </button>
            </div>

            {/* FAQ Items */}
            <div className="space-y-4">
              {items.map((item) => (
                <FAQItemComponent
                  key={item.id}
                  item={item}
                  isOpen={openItems.has(item.id)}
                  onToggle={() => toggleItem(item.id)}
                />
              ))}
            </div>
            
            {/* CTA */}
            <div className="mt-8 p-6 glass-card text-center">
              <h4 className="text-lg font-semibold text-white mb-3">
                Have more questions?
              </h4>
              <a 
                href="/contact" 
                className="inline-flex items-center gap-2 glass-button glass-button-gold hover:scale-105 transition-all duration-300"
              >
                Get in touch →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}